/**
 * "Frequentes": the symbols this person presses most. Each press adds 1 to a
 * count that slowly fades (half-life of 30 days), so the screen follows
 * current habits instead of being stuck with last year's favourites.
 * Until there is enough use, a starter set of core words fills the screen.
 */
import { LIMITS } from '@shared/constants';
import type { Usage } from '@shared/schemas';

export const FREQUENT_SLOTS = 12;
const HALF_LIFE_MS = 30 * 24 * 60 * 60 * 1000;

/** Core vocabulary shown while there is little use: I want, yes, no, more, help, stop… */
export const STARTER_IDS = [
  'base-1', 'base-2', 'base-3', 'base-8', 'base-7', 'base-10',
  'base-57', 'base-45', 'base-11', 'base-12', 'base-31', 'base-32',
];

export function score(entry: Usage[string], now = Date.now()): number {
  return entry.c * 0.5 ** (Math.max(0, now - entry.t) / HALF_LIFE_MS);
}

/** Records one press of `id`. Returns a new object (the store compares by reference). */
export function recordUse(usage: Usage, id: string, now = Date.now()): Usage {
  const prev = usage[id];
  const c = Math.round(((prev ? score(prev, now) : 0) + 1) * 1000) / 1000;
  const next: Usage = { ...usage, [id]: { c, t: now } };
  const ids = Object.keys(next);
  if (ids.length <= LIMITS.maxUsageEntries) return next;
  // Forget the least used symbols.
  ids.sort((a, b) => score(next[b]!, now) - score(next[a]!, now));
  return Object.fromEntries(ids.slice(0, LIMITS.maxUsageEntries).map((k) => [k, next[k]!]));
}

/** Most used existing symbols first, then starter words to fill the screen. */
export function frequentIds(usage: Usage, exists: (id: string) => boolean, now = Date.now(), slots = FREQUENT_SLOTS): string[] {
  const used = Object.entries(usage)
    .filter(([id]) => exists(id))
    .sort(([, a], [, b]) => score(b, now) - score(a, now) || b.t - a.t)
    .slice(0, slots)
    .map(([id]) => id);
  const fill = STARTER_IDS.filter((id) => !used.includes(id) && exists(id)).slice(0, slots - used.length);
  return [...used, ...fill];
}
